import { useSyncExternalStore } from "react";

/** Something that wants the playhead every frame, written to imperatively. */
export type PlayheadTarget = (seconds: number) => void;

/**
 * How far the playhead moves before React hears about it. Coarse enough not to
 * cost anything, frequent enough that a disabled button or a set of visible
 * clips is never visibly stale.
 */
export const PLAYHEAD_PUBLISH_STEP = 1 / 12;

/**
 * The playhead, kept outside React.
 *
 * It used to be state in App, published twelve times a second. Each publish
 * re-rendered App, and because everything under App takes callbacks written
 * inline, everything under App re-rendered with it: the stage, every field in
 * the inspector, the ruler, every clip. React DevTools counted two hundred
 * renders of each in the time it took a clip to play ten seconds.
 *
 * Now the value lives here. The loop writes it every frame; things that must
 * move smoothly (the slider, the readout, the timeline marker) register a
 * target and are written straight to the DOM; the few things that need it as
 * a React value subscribe with usePlayhead and are the only things that
 * re-render. App itself never re-renders during playback.
 */
export class PlayheadStore {
  private exact = 0;
  private coarse = 0;
  private readonly targets = new Set<PlayheadTarget>();
  private readonly listeners = new Set<() => void>();

  /** The precise position, for anything that reads it on demand. */
  get now(): number {
    return this.exact;
  }

  /** Every frame: targets hear every value, React only every step. */
  advance(seconds: number): void {
    this.exact = seconds;
    for (const target of this.targets) target(seconds);
    if (Math.abs(seconds - this.coarse) >= PLAYHEAD_PUBLISH_STEP) this.publish(seconds);
  }

  /** A deliberate move, so React hears about it at once. */
  jump(seconds: number): void {
    this.exact = seconds;
    for (const target of this.targets) target(seconds);
    this.publish(seconds);
  }

  /** Register a per-frame target. It is written immediately, then every frame. */
  target(write: PlayheadTarget): () => void {
    this.targets.add(write);
    write(this.exact);
    return () => {
      this.targets.delete(write);
    };
  }

  private publish(seconds: number): void {
    this.coarse = seconds;
    for (const listener of this.listeners) listener();
  }

  // The useSyncExternalStore contract. Bound so they can be passed as-is.
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  readonly snapshot = (): number => this.coarse;
}

/** The coarse playhead as a React value. Re-renders the caller once per step. */
export function usePlayhead(playhead: PlayheadStore): number {
  return useSyncExternalStore(playhead.subscribe, playhead.snapshot, playhead.snapshot);
}

/**
 * Something derived from the playhead, re-rendering only when the derived
 * value changes. The stage uses it for the set of visible clips: that set
 * changes when the playhead crosses a clip boundary, not twelve times a second.
 * The result must compare by value, so return a primitive.
 */
export function usePlayheadDerived<T extends string | number | boolean | null>(
  playhead: PlayheadStore,
  derive: (seconds: number) => T,
): T {
  const read = () => derive(playhead.snapshot());
  return useSyncExternalStore(playhead.subscribe, read, read);
}
